import { useContext } from 'react';
import Head from 'next/head';

import config from 'config';
import { commonMetatags } from 'consts/metatags';
import { ArticleCategory, ArticlePreview as ArticlePreviewType } from 'types/models/Article';
import { ConfigContext } from '@providers/ConfigProvider';
import { FilesContext } from '@providers/FilesProvider';
import StaticLayout from '@layouts/StaticLayout';
import Description from '@templates/Description';
import Tags from '@components/Tags';
import ArticleCategoryPreview from '@components/ArticleCategoryPreview';
import resolveImageUrl from 'utils/resolveImageUrl';

const { routes } = config;

type Props = ArticleCategory & {
  mainArticle: ArticlePreviewType | null;
  articles: ArticlePreviewType[];
  tagId?: number | null;
  tagSlug?: string;
  page: number;
  total: number;
  raw?: boolean;
};

export const ArticlesView = ({
  slug: categorySlug,
  seoData,
  tags,
  tagId,
  tagSlug,
  page,
  mainArticle,
  articles,
  total,
  raw,
}: Props): JSX.Element => {
  const { appUrl, contact } = useContext(ConfigContext);
  const { getShopImageConfigs } = useContext(FilesContext);
  const [logoOg] = getShopImageConfigs(['logo-og']);
  const logoOgUrl = logoOg ? resolveImageUrl(logoOg) : '';

  return (
    <>
      {!raw && (
        <Head>
          {commonMetatags(
            contact,
            `${appUrl}${routes.articles.href}/${categorySlug}${tagSlug ? `/${tagSlug}` : ''}`,
            seoData?.title,
            seoData?.metaDescription,
            logoOgUrl,
            false,
            true
          )}
        </Head>
      )}
      <StaticLayout>
        {seoData?.description && (
          <StaticLayout.Heading>
            <Description data={seoData.description} />
          </StaticLayout.Heading>
        )}
        <Tags
          items={tags.map(({ id, name, slug }) => ({
            id,
            name,
            href: { pathname: routes.articles.category.tag.href, query: { category: categorySlug, tag: slug } },
          }))}
          activeId={tagId || undefined}
        />
        <ArticleCategoryPreview
          mainArticle={mainArticle || undefined}
          articles={articles}
          slug={categorySlug}
          total={total}
          page={page}
          tag={tags.find(({ id }) => id === tagId)?.slug}
          raw={raw}
        />
      </StaticLayout>
    </>
  );
};

export default ArticlesView;
